import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, AlertCircle, CheckCircle2, Copy, Database } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { populateCuratedVisaData } from '@/utils/populateCuratedVisaData';

export default function AdminSetup() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isGranting, setIsGranting] = useState(false);
  const [isPopulatingVisa, setIsPopulatingVisa] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [hasAdminRole, setHasAdminRole] = useState(false);
  const [grantToken, setGrantToken] = useState('');

  const checkCurrentUser = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: 'Not Logged In',
          description: 'Please log in first to set up admin access.',
          variant: 'destructive',
        });
        navigate('/auth');
        return;
      }

      setCurrentUserId(user.id);

      // Check if user already has admin role
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();

      if (roleData) {
        setHasAdminRole(true);
        toast({
          title: 'Already Admin',
          description: 'You already have admin access!',
        });
      }
    } catch (error: any) {
      console.error('Error checking user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const sqlCommand = currentUserId ? `
-- Grant admin role to your user
INSERT INTO user_roles (user_id, role)
VALUES ('${currentUserId}', 'admin')
ON CONFLICT (user_id, role) DO NOTHING;
  `.trim() : '';

  const copyToClipboard = () => {
    navigator.clipboard.writeText(sqlCommand);
    toast({
      title: 'Copied!',
      description: 'SQL command copied to clipboard',
    });
  };

  const handleGrantAdmin = async () => {
    setIsGranting(true);
    try {
      const { data, error } = await supabase.functions.invoke('grant-admin', { body: grantToken ? { token: grantToken } : {} });
      if (error) throw error;
      if (data?.success) {
        setHasAdminRole(true);
        toast({ title: 'Admin Granted', description: 'You now have admin access.' });
      } else if (data?.error) {
        toast({ title: 'Cannot Grant Admin', description: data.error, variant: 'destructive' });
      }
    } catch (err: any) {
      console.error('Grant admin failed', err);
      toast({ title: 'Grant Failed', description: err.message, variant: 'destructive' });
    } finally {
      setIsGranting(false);
    }
  };

  const handlePopulateVisaData = async () => {
    setIsPopulatingVisa(true);
    try {
      await populateCuratedVisaData();
      toast({ 
        title: 'Success!', 
        description: 'Curated visa data has been populated for most visited countries in Asia and Africa.'
      });
    } catch (err: any) {
      console.error('Failed to populate visa data:', err);
      toast({ 
        title: 'Population Failed', 
        description: err.message, 
        variant: 'destructive' 
      });
    } finally {
      setIsPopulatingVisa(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/5 p-4 md:p-8">
      <Helmet>
        <title>Admin Setup | OdyVoy</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <Shield className="h-16 w-16 text-primary mx-auto mb-4" />
          <h1 className="text-4xl font-bold mb-2">Admin Access Setup</h1>
          <p className="text-muted-foreground">Grant yourself admin privileges to access blog management</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Step 1: Get Your User ID</CardTitle>
            <CardDescription>
              Click the button below to retrieve your user ID
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button onClick={checkCurrentUser} disabled={isLoading} className="w-full">
              {isLoading ? 'Checking...' : 'Get My User ID'}
            </Button>

            {currentUserId && (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription className="font-mono text-xs break-all">
                  Your User ID: {currentUserId}
                </AlertDescription>
              </Alert>
            )}

            {hasAdminRole && (
              <Alert>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-600">
                  ✓ You already have admin access! You can now access the blog admin pages.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {currentUserId && !hasAdminRole && (
          <Card>
            <CardHeader>
              <CardTitle>Step 2: Grant Admin Access</CardTitle>
              <CardDescription>
                Run this SQL command in your Supabase SQL Editor
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  You can grant admin automatically below (recommended). If that fails, run the SQL in the editor.
                </AlertDescription>
              </Alert>

              <Button onClick={handleGrantAdmin} disabled={isGranting} className="w-full">
                {isGranting ? 'Granting admin…' : 'Grant Admin Automatically'}
              </Button>

              <div className="space-y-2">
                <label className="text-sm font-medium">Optional admin grant token</label>
                <Input
                  placeholder="Enter token (if provided)"
                  value={grantToken}
                  onChange={(e) => setGrantToken(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">Use this only if instructed. It allows granting admin even if one already exists.</p>
              </div>

              <div className="relative">
                <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-xs">
                  <code>{sqlCommand}</code>
                </pre>
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={copyToClipboard}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">Instructions:</p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2">
                  <li>Copy the SQL command above</li>
                  <li>Open your Supabase SQL Editor (button below)</li>
                  <li>Paste and run the command</li>
                  <li>Refresh this page to verify admin access</li>
                </ol>
              </div>

              <Button
                variant="default"
                className="w-full"
                onClick={() => window.open('https://supabase.com/dashboard/project/htvdordrajveyklejsvs/sql/new', '_blank')}
              >
                Open Supabase SQL Editor
              </Button>
            </CardContent>
          </Card>
        )}

        {hasAdminRole && (
          <Card>
            <CardHeader>
              <CardTitle>🎉 You're All Set!</CardTitle>
              <CardDescription>
                You now have access to all admin pages
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <Button
                  variant="default"
                  className="w-full"
                  onClick={() => navigate('/blog-admin')}
                >
                  Go to Blog Admin
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/blog')}
                >
                  Back to Blog
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Admin Pages Overview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <h4 className="font-semibold text-sm">/blog-admin</h4>
              <p className="text-sm text-muted-foreground">
                Main dashboard to view, edit, and delete all blog posts. Shows published and draft status.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-semibold text-sm">/blog-admin/new</h4>
              <p className="text-sm text-muted-foreground">
                Create new blog posts with a rich text editor. Set title, excerpt, category, tags, and publish settings.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-semibold text-sm">/blog-admin/edit/:id</h4>
              <p className="text-sm text-muted-foreground">
                Edit existing blog posts. Same interface as creating new posts, but pre-filled with existing content.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-semibold text-sm">/blog-admin/analytics</h4>
              <p className="text-sm text-muted-foreground">
                View blog performance metrics including total views, daily trends, and top-performing articles.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-semibold text-sm">/newsletter-admin</h4>
              <p className="text-sm text-muted-foreground">
                Manage weekly newsletter digests. Send test emails and set up automated scheduling.
              </p>
            </div>
          </CardContent>
        </Card>

        {hasAdminRole && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Database Setup
              </CardTitle>
              <CardDescription>
                One-time setup tasks for database population
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  These tasks should only be run once during initial setup. Running them multiple times is safe but unnecessary.
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Populate Curated Visa Data</h4>
                <p className="text-sm text-muted-foreground">
                  Populates accurate visa requirements for most visited countries in Asia and Africa (Thailand, Japan, Singapore, UAE, Indonesia, Vietnam, Malaysia, Egypt, Morocco, South Africa, Kenya, Tanzania).
                </p>
                <Button 
                  onClick={handlePopulateVisaData} 
                  disabled={isPopulatingVisa}
                  variant="secondary"
                  className="w-full"
                >
                  {isPopulatingVisa ? 'Populating...' : 'Populate Curated Visa Data'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
